import { useCallback, useEffect, useRef, useState } from "react";
import { apiClient } from "../shared/api/AllApi";
import { isNetworkError } from "../shared/lib/connectivity";
import { isAbortError } from "../shared/lib/request";
import { loadCatalogCache, saveCatalogCache } from "../features/pos/lib/posCatalogCache";

const CACHE_TTL_MS = 5 * 60 * 1000;

const normalizeDefault = (payload) => (Array.isArray(payload) ? payload : []);

const normalizeAccountants = (payload) => {
  if (payload?.success && Array.isArray(payload.data)) {
    return payload.data;
  }

  if (Array.isArray(payload)) {
    return payload;
  }

  if (payload && typeof payload === "object" && payload.accountants) {
    return Object.entries(payload.accountants).map(([id, value]) => ({
      id,
      ...value,
    }));
  }

  if (payload && typeof payload === "object") {
    return Object.entries(payload).map(([id, value]) => ({ id, ...value }));
  }

  return [];
};

const CATALOG_CONFIG = {
  products: { endpoint: "/products", normalize: normalizeDefault },
  units: { endpoint: "/units", normalize: normalizeDefault },
  categories: { endpoint: "/categories", normalize: normalizeDefault },
  customers: { endpoint: "/customers", normalize: normalizeDefault },
  accountants: { endpoint: "/accountants", normalize: normalizeAccountants },
};

const catalogStore = {
  products: { data: null, fetchedAt: 0, promise: null, controller: null, fromOffline: false },
  units: { data: null, fetchedAt: 0, promise: null, controller: null, fromOffline: false },
  categories: { data: null, fetchedAt: 0, promise: null, controller: null, fromOffline: false },
  customers: { data: null, fetchedAt: 0, promise: null, controller: null, fromOffline: false },
  accountants: { data: null, fetchedAt: 0, promise: null, controller: null, fromOffline: false },
};

const isFresh = (entry) =>
  Boolean(entry.data) && !entry.fromOffline && Date.now() - entry.fetchedAt < CACHE_TTL_MS;

const hydrateFromIdb = async (key) => {
  const entry = catalogStore[key];
  if (!entry || entry.data) return entry?.data || null;
  const cached = await loadCatalogCache(key);
  if (cached?.data != null) {
    entry.data = cached.data;
    entry.fetchedAt = cached.savedAt || 0;
    entry.fromOffline = true;
    return entry.data;
  }
  return null;
};

const fetchCatalog = async (key, force = false) => {
  const config = CATALOG_CONFIG[key];
  const entry = catalogStore[key];
  const endpoint = config?.endpoint;
  const normalize = config?.normalize || normalizeDefault;

  if (!endpoint || !entry) {
    throw new Error(`Unsupported catalog key: ${key}`);
  }

  if (!force && isFresh(entry)) {
    return entry.data;
  }

  if (!force && entry.promise) {
    return entry.promise;
  }

  // Prefer durable IDB cache while waiting for network (or if already offline).
  if (!force && !entry.data) {
    await hydrateFromIdb(key);
  }

  // Cancel any in-flight fetch for this catalog key before starting a new one.
  if (entry.controller) {
    entry.controller.abort();
  }
  const controller = new AbortController();
  entry.controller = controller;

  entry.promise = apiClient
    .get(endpoint, { signal: controller.signal })
    .then(async (response) => {
      const normalized = normalize(response.data);
      entry.data = normalized;
      entry.fetchedAt = Date.now();
      entry.fromOffline = false;
      saveCatalogCache(key, normalized);
      return entry.data;
    })
    .catch(async (err) => {
      if (isAbortError(err)) throw err;

      // Network down: serve IndexedDB / memory snapshot so POS can still sell.
      if (isNetworkError(err)) {
        if (!entry.data) {
          await hydrateFromIdb(key);
        }
        if (entry.data) {
          entry.fromOffline = true;
          return entry.data;
        }
      }
      throw err;
    })
    .finally(() => {
      if (entry.controller === controller) {
        entry.controller = null;
      }
      entry.promise = null;
    });

  return entry.promise;
};

export const invalidateCatalog = (key) => {
  if (key && catalogStore[key]) {
    catalogStore[key].controller?.abort();
    catalogStore[key] = { data: null, fetchedAt: 0, promise: null, controller: null, fromOffline: false };
    return;
  }

  Object.keys(catalogStore).forEach((catalogKey) => {
    catalogStore[catalogKey].controller?.abort();
    catalogStore[catalogKey] = { data: null, fetchedAt: 0, promise: null, controller: null, fromOffline: false };
  });
};

export const useCatalogData = (key) => {
  const entry = catalogStore[key];
  const hasCache = Boolean(entry?.data);
  const [data, setData] = useState(entry?.data || []);
  // If we already have cache, don't block the UI with a full-page spinner.
  const [loading, setLoading] = useState(!hasCache);
  const [error, setError] = useState(null);
  const [fromOffline, setFromOffline] = useState(Boolean(entry?.fromOffline));
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const load = useCallback(
    async (force = false) => {
      const cached = catalogStore[key]?.data;
      // Show spinner only when we have nothing to display yet.
      if (!cached || force) {
        if (mountedRef.current) setLoading(true);
      }
      if (mountedRef.current) setError(null);
      try {
        // Warm from IDB first so offline/slow networks still show products quickly.
        if (!cached) {
          const hydrated = await hydrateFromIdb(key);
          if (hydrated && mountedRef.current) {
            setData(Array.isArray(hydrated) ? hydrated : []);
            setFromOffline(true);
            setLoading(false);
          }
        }

        const result = await fetchCatalog(key, force);
        if (!mountedRef.current) return result;
        setData(result || []);
        setFromOffline(Boolean(catalogStore[key]?.fromOffline));
        return result;
      } catch (err) {
        if (isAbortError(err)) return cached || [];
        if (mountedRef.current) {
          setError(err);
          if (!cached && !catalogStore[key]?.data) setData([]);
          else if (catalogStore[key]?.data) {
            setData(catalogStore[key].data);
            setFromOffline(true);
          }
        }
        return catalogStore[key]?.data || cached || [];
      } finally {
        if (mountedRef.current) setLoading(false);
      }
    },
    [key]
  );

  useEffect(() => {
    load(false);
  }, [load]);

  const refresh = useCallback(() => load(true), [load]);

  return { data, loading, error, refresh, fromOffline };
};

export const useProductsCatalog = () => useCatalogData("products");
export const useUnitsCatalog = () => useCatalogData("units");
export const useCategoriesCatalog = () => useCatalogData("categories");
export const useCustomersCatalog = () => useCatalogData("customers");
export const useAccountantsCatalog = () => useCatalogData("accountants");
